import {
  SkyBadge,
  SkyButton,
  SkyLink,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
  SkyTooltip,
} from "@skypointcloud/skypoint-ui";
import { forwardRef, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { ExternalLinkIcon } from "@/lib/icons";
import { absolutePath } from "@/lib/routes";
import type {
  CountFact,
  LandingFacts,
  LastRunFact,
  RepositoryFact,
} from "@/lib/transformations/landingFacts";
import { statusLineText, type StatusLine } from "@/lib/transformations/landingStatus";
import {
  gitRefTerm,
  repoHostName,
  repoShortName,
  repoWebUrl,
} from "@/lib/transformations/repoFileUrl";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useNow } from "@/hooks/use-now";
import { endedWhen } from "@/lib/runs/runActivity";
import { BreakableAddress, RepositoryWithheld } from "./RepositoryName";

/**
 * The facts strip under the Transformations title (owner feedback 2026-09-28; it replaces the
 * Session 11 one-line "Repository connected · main · last run succeeded … · 4 models · 23 tests
 * passed", which read as plain text, and before it the always-on success banner). Four facts in
 * one outlined definition list, each a muted term over its value:
 *
 * - Repository: the repository's name as a link, "Branch" and the ref in monospace (round 7
 *   T-38), and the connected state as a badge (colour and word).
 * - Last run: the outcome badge (the History view's words) and its age, a link to the run; the
 *   age carries its absolute instant in a tooltip.
 * - Models: "4 of 5 built", the last run's built models of the project's (the switch's number).
 * - Tests: "23 of 33 passed", with a danger badge for failed tests and a warning one for warned.
 *   "Not in this run 7" carries a hint, on hover and focus, of how many of those 7 have a result
 *   from an earlier run (#1213 round 4).
 *
 * The values come from the pure `landingFacts()`; the subtle "Details" opens the dbt project
 * drawer. The list keeps the stable id `transformation-status-line`: it is the `aria-describedby`
 * target of "Run all dbt jobs now" when nothing can run and no bar is shown.
 *
 * Live updates (UX §3.5, the S10 rule): the strip re-renders on every poll, so it is NOT a live
 * region (its "2 minutes ago" would be re-read). `SkyBadge` renders `role="status"`, so each badge
 * is keyed by its word: a changed outcome mounts a new badge (not announced) rather than changing
 * the text of a live one, and the polite region below stays the one voice. A separate, always-mounted polite region receives
 * the status sentence (`statusLineText`) only when the outcome changes while a run is in flight:
 * queued, running, and the finish. Nothing is announced on the first read, and no toast is raised.
 */

export const TRANSFORMATION_STATUS_LINE_ID = "transformation-status-line";

export interface TransformationStatusLineProps {
  line: StatusLine;
  facts: LandingFacts;
  /** The newest run's id; with the state it keys the announcement. */
  runId: string | null;
  onDetails(): void;
}

/**
 * What the polite region says: the line, but only for an outcome change that involves a run in
 * flight (the change a user waiting on the page wants to hear). State adjusted during render.
 */
function useOutcomeAnnouncement(line: StatusLine, runId: string | null): string {
  const zone = useDisplayTz();
  const key = line.state === "loading" ? null : `${runId ?? ""}|${line.state}`;
  const [seen, setSeen] = useState<{ key: string; inFlight: boolean; text: string } | null>(null);
  if (key !== null && seen?.key !== key) {
    const announce = seen !== null && (seen.inFlight || line.inFlight);
    setSeen({
      key,
      inFlight: line.inFlight,
      text: announce
        ? `Transformations: ${statusLineText(line, (iso) => endedWhen(iso, zone))}`
        : (seen?.text ?? ""),
    });
  }
  return seen?.text ?? "";
}

function Fact({ name, term, children }: { name: string; term: string; children: ReactNode }) {
  return (
    <div className="flex min-w-40 flex-col gap-1" data-testid={`transformation-fact-${name}`}>
      <dt>
        <SkyText size={200} color="muted">
          {term}
        </SkyText>
      </dt>
      <dd className="flex min-w-0 flex-wrap items-center gap-2">{children}</dd>
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return (
    <SkyText size={300} color="muted">
      {children}
    </SkyText>
  );
}

/**
 * Round 7 T-38 (owner screenshot 2026-10-02: "Repository main Connected" put the branch where the
 * repository belongs): `owner/repo` as a link to its page (a new tab, the external link icon,
 * never the address as text), then "Branch main" (the ref's word from `gitRefTerm`), then the
 * connected state. #1085: an address on a host skyData does not know reads as saved, and no
 * address at all reads "Not set".
 */
function RepositoryValue({ repository }: { repository: LandingFacts["repository"] }) {
  if (repository.state === "unknown") return <Muted>Could not be read</Muted>;
  const full = repoShortName(repository.repoUrl);
  const href = repoWebUrl(repository.repoUrl);
  const host = repoHostName(repository.repoUrl);
  return (
    <>
      {full && href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          title={full}
          aria-label={`${full}, opens the repository on ${host ?? "its host"} in a new tab`}
          className="inline-flex min-w-0 max-w-full items-center gap-1 underline-offset-2 hover:underline"
          data-testid="transformation-fact-repository-link"
        >
          <SkyText size={300} color="accent" className="min-w-0 break-all">
            {full}
          </SkyText>
          <ExternalLinkIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        </a>
      ) : repository.repoUrl ? (
        <BreakableAddress
          repoUrl={repository.repoUrl}
          size={300}
          block={false}
          testId="transformation-fact-repository-address"
        />
      ) : repository.state === "connected" && repository.withheld ? (
        <RepositoryWithheld size={300} testId="transformation-fact-repository-withheld" />
      ) : repository.state === "connected" ? (
        <Muted>Not set</Muted>
      ) : null}
      {repository.ref ? (
        <span
          className="inline-flex min-w-0 items-baseline gap-1"
          data-testid="transformation-fact-repository-branch"
        >
          <SkyText size={300} color="muted">
            {gitRefTerm(repository.ref)}
          </SkyText>
          <SkyText size={300} className="min-w-0 break-all">
            <span className="font-mono">{repository.ref}</span>
          </SkyText>
        </span>
      ) : null}
      <SkyBadge
        key={repository.state}
        appearance="tint"
        color={repository.state === "connected" && !repository.cloneFailed ? "success" : "warning"}
        testId="transformation-fact-repository-state"
      >
        {repositoryStateWord(repository)}
      </SkyBadge>
    </>
  );
}

/** #1674: "Connected" only while the newest run did not stop at the checkout. */
function repositoryStateWord(repository: RepositoryFact): string {
  if (repository.state !== "connected") return "Not connected";
  return repository.cloneFailed ? "Could not clone" : "Connected";
}

/** The way to the last run: when it ran (its full time on hover), else View run; none without one. */
function lastRunLink(run: LastRunFact, href: string | null, zone: string, now: Date): ReactNode {
  if (!href) return null;
  if (run.at) {
    return (
      <SkyTooltip content={formatInstant(run.at, zone, "seconds", { named: true })}>
        <SkyLink as={Link} href={href} testId="transformation-fact-last-run-link">
          {run.started ? "started " : null}
          <time dateTime={run.at} data-testid="transformation-status-line-when">
            {endedWhen(run.at, zone, now)}
          </time>
        </SkyLink>
      </SkyTooltip>
    );
  }
  return (
    <SkyLink as={Link} href={href} testId="transformation-fact-last-run-link">
      View run
    </SkyLink>
  );
}

function LastRunValue({ run }: { run: LastRunFact }) {
  const zone = useDisplayTz();
  // #1260: the age moves while the page stays open.
  const now = useNow();
  if (run.word === null) return <Muted>{run.note}</Muted>;
  const href = run.runId ? absolutePath(`/runs/${run.runId}`) : null;
  return (
    <>
      <SkyBadge
        key={run.word}
        appearance="tint"
        color={run.color}
        testId="transformation-fact-last-run-outcome"
      >
        {run.word}
      </SkyBadge>
      {run.note ? <Muted>{run.note}</Muted> : null}
      {lastRunLink(run, href, zone, now)}
    </>
  );
}

/** A count's text: none when empty, muted when it is a note, else the number. */
function countText(fact: CountFact): ReactNode {
  if (fact.text === "") return null;
  if (fact.muted) return <Muted>{fact.text}</Muted>;
  return (
    <SkyText size={300} font="numeric">
      {fact.text}
    </SkyText>
  );
}

function CountValue({ fact, name }: { fact: CountFact; name: string }) {
  return (
    <>
      {countText(fact)}
      {fact.badges.map((badge) => {
        const chip = (
          <SkyBadge
            key={badge.label}
            appearance="tint"
            color={badge.color}
            testId={`transformation-fact-${name}-${badge.color}`}
          >
            {badge.label}
          </SkyBadge>
        );
        if (!badge.hint) return chip;
        // #1213 round 4: the trigger takes focus, so a keyboard user can open the hint too, and a
        // screen reader reads it in place (the Runs list's Started by precedent, `RunsTable.tsx`).
        return (
          <SkyTooltip key={badge.label} content={badge.hint}>
            <span
              tabIndex={0}
              className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`transformation-fact-${name}-hint`}
            >
              {chip}
              <span className="sr-only">{` ${badge.hint}`}</span>
            </span>
          </SkyTooltip>
        );
      })}
    </>
  );
}

export const TransformationStatusLine = forwardRef<
  HTMLButtonElement,
  TransformationStatusLineProps
>(function TransformationStatusLine({ line, facts, runId, onDetails }, detailsRef) {
  const announcement = useOutcomeAnnouncement(line, runId);

  return (
    // One outlined surface: the facts wrap by the width they get (the sidebar changes it, so no
    // viewport breakpoint), each at least 10rem; Details sits at the end of the row.
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2 rounded-md border border-border bg-card px-4 py-3">
      <span className="sr-only" aria-live="polite" data-testid="transformation-status-announcement">
        {announcement}
      </span>
      {line.state === "loading" ? (
        <SkySkeleton
          testId="transformation-status-line-loading"
          aria-busy="true"
          className="min-w-0 flex-1"
        >
          <SkySkeletonItem size={44} />
        </SkySkeleton>
      ) : (
        <dl
          id={TRANSFORMATION_STATUS_LINE_ID}
          data-testid="transformation-status-line"
          data-state={line.state}
          className="flex min-w-0 flex-1 flex-wrap gap-x-10 gap-y-3 break-words"
        >
          <Fact name="repository" term="Repository">
            <RepositoryValue repository={facts.repository} />
          </Fact>
          <Fact name="last-run" term="Last run">
            <LastRunValue run={facts.lastRun} />
          </Fact>
          <Fact name="models" term="Models">
            <CountValue fact={facts.models} name="models" />
          </Fact>
          <Fact name="tests" term="Tests">
            <CountValue fact={facts.tests} name="tests" />
          </Fact>
        </dl>
      )}
      <SkyButton
        ref={detailsRef}
        appearance="subtle"
        size="small"
        onClick={onDetails}
        testId="transformation-details"
      >
        Details
      </SkyButton>
    </div>
  );
});
